import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';

import { urlUtils } from '@blaze-shared/platform-util';
import { measureTextSize, resizeRxUtils, toFloat } from '@blaze-shared/shared-util';
import { FileResult, getItemTypeIcon } from '@blaze-shared/uploader';

import { type UITableCellStyling } from '../../elements/table/table-cell-styling';
import { FileService } from '../../services/file.service';

interface StyleData {
  font: string;
  fileMinWidth: number;
  fileHeight: number;
  gap: number;
  containerWidth: number;
  containerHeight: number;
}
@Component({
  selector: 'blaze-file-column-value',
  templateUrl: './file-column-value.component.html',
  styleUrls: ['./file-column-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FileColumnValueComponent implements OnChanges {
  private fileService = inject(FileService);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);

  @Input() value: string | string[] | unknown = '';
  @Input() isSecure!: boolean;
  @Input() useExternalClickAction? = false;
  @Input() isMultipleValue? = false;
  @Input() cellStyles? = {} as UITableCellStyling;
  @Input() actionsAlwaysVisible = false;

  @Output() clickAction = new EventEmitter();

  @ViewChild('listElementRef', { static: true }) listElementRef!: ElementRef<HTMLUListElement>;
  @ViewChild('placeholderTag', { static: true }) placeholderTagRef!: ElementRef<HTMLSpanElement>;
  @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;

  private fitToContainer$$ = new BehaviorSubject(false);
  private files$$ = new BehaviorSubject<FileResult[]>([]);
  readonly getFileUrlHref = urlUtils.getFileUrlHref;
  displayFiles$: Observable<FileResult[]>;

  @Input() set fitToContainer(v: boolean) {
    this.fitToContainer$$.next(v);
  }

  set files(val: FileResult[]) {
    this.files$$.next(val);
  }
  get files(): FileResult[] {
    return this.files$$.value ?? [];
  }

  @HostBinding('class.empty') get isEmpty(): boolean {
    return !this.files.length;
  }

  @HostBinding('class.min-size') get addMinSize(): boolean {
    return (this.isEmpty && this.useExternalClickAction) || false;
  }

  get canAddValue(): boolean {
    return !!(
      this.useExternalClickAction &&
      (this.isMultipleValue || (!this.isMultipleValue && !this.value))
    );
  }

  get isMenuOpen(): boolean {
    return !!this.menuTrigger?.menuOpen;
  }

  constructor() {
    this.displayFiles$ = combineLatest([
      this.fitToContainer$$,
      this.files$$,
      resizeRxUtils.fromResizeObserver(this.elementRef.nativeElement),
    ]).pipe(
      map(([fitToContainer, files]) => {
        if (!fitToContainer) {
          return files;
        }
        const filesToDisplay: FileResult[] = [];
        const styleData = this.getStyleData();
        const maxNumberOfLines = Math.max(
          1,
          Math.floor(styleData.containerHeight / (styleData.fileHeight + styleData.gap)),
        );
        let linesUsed = 1;
        let remainingLineWidth = styleData.containerWidth;
        let index = 0;

        let currFileWidth;
        do {
          if (!files[index]) {
            break;
          }
          currFileWidth =
            Math.ceil(this.calculateFileElWidth(files[index].filename, styleData)) + 4;
          if (
            currFileWidth < remainingLineWidth ||
            remainingLineWidth === styleData.containerWidth
          ) {
            // we can insert it into same line when it will fit or line is empty
            filesToDisplay.push(files[index]);
            remainingLineWidth = remainingLineWidth - currFileWidth - styleData.gap;
          } else if (linesUsed < maxNumberOfLines) {
            // we can push it into new line
            filesToDisplay.push(files[index]);
            remainingLineWidth = styleData.containerWidth - currFileWidth - styleData.gap;
            linesUsed++;
          } else {
            // we cannot do anything
            break;
          }
          index++;
        } while (index < files.length);

        return filesToDisplay;
      }),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.files = this.processFiles(changes['value'].currentValue);
    }
  }

  downloadFile(event: MouseEvent, fileUrl: string | undefined): void {
    if (!this.isSecure || !fileUrl) {
      // Non-secure files are handled by default href's behavior
      return;
    }

    event.preventDefault();
    this.fileService.downloadSecureFile(fileUrl);
  }

  deleteFileFromList(event: Event, file?: FileResult, element?: HTMLElement): void {
    this.clickAction.emit({
      event,
      file,
      element,
      isSecure: this.isSecure,
      isRemoveFileAction: true,
    });
  }

  externalClickAction(
    event: Event,
    file?: FileResult,
    element?: HTMLElement,
    isAddFileAction?: boolean,
  ): void {
    this.clickAction.emit({ event, file, element, isSecure: this.isSecure, isAddFileAction });
  }

  preventAction(event: Event): void {
    event?.preventDefault();
    event.stopImmediatePropagation();
  }

  private getStyleData(): StyleData {
    const { offsetWidth: fileWidth, offsetHeight: fileHeight } =
      this.placeholderTagRef.nativeElement;
    const { offsetWidth: containerWidth, offsetHeight: containerHeight } =
      this.elementRef.nativeElement;

    const listStyles = window.getComputedStyle(this.listElementRef.nativeElement);

    const fileStyles = window.getComputedStyle(this.placeholderTagRef.nativeElement);
    const filePaddingX =
      Math.ceil(toFloat(fileStyles.paddingLeft)) + Math.ceil(toFloat(fileStyles.paddingRight));

    const containerStyles = window.getComputedStyle(this.elementRef.nativeElement);
    const containerPaddingY = Math.floor(
      toFloat(containerStyles.getPropertyValue('--b-t-row-padding')),
    );

    return {
      font: fileStyles.font,
      gap: Math.ceil(toFloat(listStyles.gap)),
      fileMinWidth: fileWidth - filePaddingX,
      fileHeight: fileHeight,
      containerWidth: containerWidth - Math.ceil(toFloat(containerStyles.paddingRight)),
      containerHeight: containerHeight - containerPaddingY,
    };
  }

  private processFiles(value: any): FileResult[] {
    const toProcess = Array.isArray(value) ? value : [value];

    return toProcess
      .filter((v) => v && typeof v === 'string')
      .map((v) => {
        const details = this.extractFileDetailsFromUrl(v);
        return details ? details : { filename: v };
      });
  }

  private extractFileDetailsFromUrl(url: string): FileResult | null {
    if (!url) {
      return null;
    }

    try {
      const urlObject = new URL(url);
      const filename = decodeURIComponent(urlObject.pathname.split('/').pop() || '');
      const nameParts = filename.split('.') || '';
      const extension = nameParts[nameParts.length - 1].toLocaleLowerCase();
      const thumbnail = getItemTypeIcon(`.${extension}`);

      return {
        filename,
        type: extension,
        thumbnail,
        url: urlObject.toString(),
      };
    } catch {
      return null;
    }
  }

  private calculateFileElWidth(value: string, styleData: StyleData): number {
    const textWidth = Math.max(
      styleData.fileMinWidth + measureTextSize(value, styleData.font).width,
    );
    return textWidth;
  }
}
